import { useId } from 'react';
import { cx } from '../cx';
import { GLYPHS } from './generated';
import {
  FUNCTION_GLYPH_NAMES,
  GLYPH_NAMES,
  type GlyphName,
  isGlyphName,
  OBJECT_GLYPH_NAMES,
} from './generated/names';

/**
 * THE OBJECT SYMBOL (symbol set v2, decided by Arif icon by icon on 2026-09-09).
 *
 * Fifteen drawings, one per object the sidebar lists plus the AI assistant, built the
 * way Apple builds an app icon's artwork rather than the way it builds an SF Symbol:
 * filled shapes in layers, each layer a body gradient with a rim light, a one-pixel
 * contour and a soft halo, the effect stack measured on the Health, Messages and Phone
 * artwork in the iOS 27 kit. The design document that Arif approved renders exactly this
 * markup (ben/OFMOS/crm-design/design-system/CRM-TILE-SYSTEM-2026-09-08.html, section 3),
 * and `generated/` IS that markup, emitted by the same tool that draws the document
 * (ben/tools/crm-glyphs/build_glyphs.py --react): nothing here is drawn by hand.
 *
 * INLINE, PER INSTANCE, WITH ITS OWN IDS - and all three are forced. Gradients, masks
 * and filters do not render through `<use>` from a shared `<symbol>` in WebKit, so the
 * markup is inlined where it is shown. Every layer carries a path, a gradient, a mask
 * and a filter by id, and ids are document-wide, so a second glyph on the page would
 * otherwise resolve the FIRST glyph's shapes (the design session hit exactly that:
 * `use` pointing at another icon). React's `useId` is the per-instance prefix; it is
 * stripped to letters and digits because the characters React puts around it are not
 * safe inside `url(#...)` in every engine.
 *
 * THE COLOUR COMES FROM WHERE IT SITS. Inside `ObjectTile` the text colour is the tile's
 * white and the body stops fall back to `currentColor`; outside a tile the same markup
 * takes the text colour like an SF Symbol in a tab bar. Second colours (the gold of the
 * coin and the trophy, the Siri ring of the AI helmet) are baked into the artwork and
 * stay in colour wherever it sits, which is what Apple's coloured artwork does too. The
 * fallbacks and the variables the other looks would set live in `index.css`.
 *
 * It is `aria-hidden`: the symbol is never the accessible name of anything. The label a
 * picker needs is `GLYPH_LABELS`, one human name per symbol.
 */
/**
 * THE NAMES COME IN TWO LISTS AND ONE UNION. `OBJECT_GLYPH_NAMES` is what a picker
 * offers an object; `FUNCTION_GLYPH_NAMES` is the sixteen rows and controls that are not
 * objects and is offered nowhere; `GLYPH_NAMES` is both, and it is what VALIDATION reads
 * - a function symbol stored on an object is still a symbol this component can draw.
 */
export { FUNCTION_GLYPH_NAMES, GLYPH_NAMES, type GlyphName, isGlyphName, OBJECT_GLYPH_NAMES };

/** The symbol's human name, for a picker's swatch and nothing else: the tile is hidden. */
export const GLYPH_LABELS: Record<GlyphName, string> = {
  pulse: 'Weekly pulse',
  call: 'Call',
  creator: 'Creator',
  person: 'Person',
  team: 'Team member',
  agency: 'Agency',
  lead: 'Lead',
  deal: 'Deal',
  enrollment: 'Enrollment',
  gameplan: 'Gameplan',
  payment: 'Payment',
  win: 'Win',
  phase: 'Phase transition',
  delivery: 'Deliveries',
  ai: 'AI assistant',
  dashboard: 'Dashboard',
  reports: 'Reports',
  tasks: 'My tasks',
  pending: 'Pending changes',
  objects: 'Objects and fields',
  settings: 'Settings',
  sidebar: 'Sidebar',
  workflows: 'Workflows',
  imports: 'Imports',
  exports: 'Exports',
  webhooks: 'Webhooks',
  inbound: 'Inbound sources',
  health: 'Health',
  members: 'Members',
  identity: 'Identity',
  instance: 'Instance',
};

export interface ObjectGlyphProps {
  readonly name: GlyphName;
  /** Any CSS length. Defaults to the icon size, so a symbol outside a tile follows its text. */
  readonly size?: string | undefined;
  /**
   * The effect stack - contour, halo, rim light - which is artwork ON A TILE. Off by
   * default: outside a tile the symbol is flat, like an SF Symbol in a tab bar or a
   * list, and `ObjectTile` is the caller that turns it on.
   */
  readonly effects?: boolean | undefined;
  readonly className?: string | undefined;
}

export function ObjectGlyph({
  name,
  size = '1.25em',
  effects = false,
  className,
}: ObjectGlyphProps) {
  const id = `g${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const Glyph = GLYPHS[name];
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      /**
       * `overflow-visible` because the halo's filter region reaches two units past the
       * grid: an SVG clips its own box by default and would cut the shadow at the edge.
       * The TILE clips it instead, at its own corner radius, which is where a glyph
       * that touches the edge should be cut.
       */
      className={cx(
        'object-glyph block shrink-0 overflow-visible',
        !effects && 'object-glyph-flat',
        className,
      )}
    >
      <Glyph id={id} />
    </svg>
  );
}
